import React, { useState } from 'react';
import { AchRelationshipsTableContext, IAchRelationshipsTableContext } from './AchRelationshipsTableContext';
import { useAsyncData } from '@shared/react';
import { useErrorNotify } from '@features/useErrorNotify';
import { getAchRelationships } from '../../api';
import { usePagination } from '@features/Pagination';
import { combineFilterWithOp } from '@shared/utils';

export interface IAchRelationshipsDesc {
    id: boolean;
    routingNumber: boolean;
    accountNumber: boolean;
    accountOwnerName: boolean;
    alias: boolean;
    createdAt: boolean;
    [key: string]: boolean;
}

export const AchRelationshipsTableProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
    const [alias, setAlias] = useState<string>('');
    const [sortFieldName, setSortFieldName] = useState('id');
    const [desc, setDesc] = useState<IAchRelationshipsDesc>({
        id: false,
        accountNumber: true,
        accountOwnerName: true,
        alias: true,
        createdAt: true,
        routingNumber: true,
    });

    const { page, pageSize, setTotalCount, paginationOptions } = usePagination();

    const {
        data: achRelationships,
        loading: achRelationshipsLoading,
        error: achRelationshipsError,
        handleRefresh: achRelationshipsRefresh,
    } = useAsyncData(
        async () => {
            const filter = {
                alias: { value: !!alias && alias + '/i', operator: '=*' },
            };
            const { items, totalResults } = await getAchRelationships({
                page: page,
                pageSize,
                filter: combineFilterWithOp(filter),
                sortBy: `${sortFieldName} ${desc[sortFieldName] ? 'desc' : ''}`,
            });
            setTotalCount(totalResults);
            return items || [];
        },
        [],
        [pageSize, page, alias],
    );

    const handleSecuritiesSort = (fieldName: string) => {
        setSortFieldName(fieldName);
        setDesc((prev) => ({ ...prev, [fieldName]: !prev[fieldName] }));
        achRelationshipsRefresh();
    };

    useErrorNotify(achRelationshipsError, 'Records');

    const value: IAchRelationshipsTableContext = {
        achRelationships,
        achRelationshipsLoading,
        paginationOptions,
        desc,
        sortFieldName,
        handleSecuritiesSort,
        setAlias,
    };

    return <AchRelationshipsTableContext.Provider value={value}>{children}</AchRelationshipsTableContext.Provider>;
};
